import type {
  Annotation,
  BundleEntry,
  CarePlan,
  Condition,
  FhirResource,
  Observation,
  Provenance,
  Task,
} from 'fhir/r4';
import moment from 'moment';

import * as Constants from '../common/constants';
import * as ConditionBuilder from '../common/fhir-builders/condition';
import * as GoalBuilder from '../common/fhir-builders/goal';
import * as ObservationBuilder from '../common/fhir-builders/observation';
import * as ProvenanceBuilder from '../common/fhir-builders/provenance';
import type FHIRProxyClient from '../common/fhirproxy-client';
import * as FHIRUtils from '../common/fhirutils';


export async function diagnoseFoodInsecurity(
  orgId: string,
  patientId: string,
  carePlanId: string,
  effectiveDate: string,
  foodInsecure: boolean,
  actorRef: { reference: string; display: string },
  updatedBy: string,
  fhirSender: FHIRProxyClient,
  screeningStatus: string
) {
  try {
    const fhirBundle: FhirResource = FHIRUtils.buildEmptyBundle('transaction');
    const bundleEntries: BundleEntry[] = [];

    //create a Food Insecurity Screening result Observation, FoodInsecurity Condition and the Goal
    //TODO: Associate Screening Questionnaire Response to this
    const fiObservation: Observation = ObservationBuilder.buildFoodInsecurityObservation(
      patientId,
      effectiveDate,
      foodInsecure,
      updatedBy
    );
    const condition: Condition = ConditionBuilder.buildFoodInsecurityCondition(
      patientId,
      fiObservation.id!,
      effectiveDate,
      foodInsecure,
      updatedBy
    );
    const goalId: string | undefined = undefined;
    const goal = GoalBuilder.buildFoodSecurityGoal(goalId, patientId, condition.id!, updatedBy);

    bundleEntries.push(FHIRUtils.buildResourceEntry(fiObservation, false));
    bundleEntries.push(FHIRUtils.buildResourceEntry(condition, false));
    bundleEntries.push(FHIRUtils.buildResourceEntry(goal, false));

    //Update CarePlan with  goalId,conditionId AND Mark careplan screening activity and associated task complete
    const screening = await markScreeningComplete(carePlanId, condition.id!, goal.id!, updatedBy, fhirSender);
    const task = screening.task;
    let carePlan = screening.carePlan;
    if (carePlan) {
      const carePlayPayloadIdentifier = carePlan?.identifier;
      /*
       * SUSPECTED DEFECT — REPORTED, NOT FIXED. `carePlan?.identifier` is
       * optional-chained on assignment and then used unguarded on the next line
       * and again in the `push` below. `CarePlan.identifier` is `0..*` in FHIR R4,
       * so a care plan saved without identifiers makes `.find` throw and the
       * screening never gets marked complete — while the `if (carePlan)` above it
       * suggests absence was considered.
       */
      const careplanstatus = carePlayPayloadIdentifier!.find(
        (obj) => obj.system === 'https://projectwell.io/fhir/identifiers/screening-status'
      );
      if (careplanstatus && careplanstatus.value !== screeningStatus) {
        careplanstatus.value = screeningStatus;
      } else if (!careplanstatus) {
        carePlayPayloadIdentifier!.push({
          system: 'https://projectwell.io/fhir/identifiers/screening-status',
          value: screeningStatus,
        });
      }
      carePlan = FHIRUtils.addUpdatedBy(carePlan, updatedBy);
      bundleEntries.push(FHIRUtils.buildResourceEntry(carePlan, false));
    }
    if (task) {
      bundleEntries.push(FHIRUtils.buildResourceEntry(task, false));
    }

    const provenancce: Provenance = ProvenanceBuilder.build(
      `CarePlan/${carePlan.id!}`,
      actorRef,
      patientId,
      'UPDATE',
      'Screen and Diagnose for Food Insecurity',
      { code: orgId, display: orgId }
    );
    bundleEntries.push(FHIRUtils.buildResourceEntry(provenancce, false));

    //now post bundle request
    fhirBundle.entry = bundleEntries;
    const response = await fhirSender.send('post', '', fhirBundle);

    return response;
  } catch (error) {
    console.log('Error in screen:diagnoseFoodInsecurity():', (error instanceof Error ? error.message : String(error)));
    throw error;
  }
}
//Update Care Plan Screening Activity to be Complete
async function markScreeningComplete(
  carePlanId: string,
  conditionId: string,
  goalId: string,
  updatedBy: string,
  fhirSender: FHIRProxyClient
) {
  let task: Task | undefined;
  const carePlanResponse = await fhirSender.send<CarePlan>(
    'get',
    'CarePlan/' + carePlanId,
    undefined,
  );
  const carePlan: CarePlan = carePlanResponse.data;
  //update carePlan with condition and goal
  if (conditionId) {
    carePlan.addresses = [
      {
        reference: 'Condition/' + conditionId,
      },
    ];
  }

  if (goalId) {
    carePlan.goal = [
      {
        reference: 'Goal/' + goalId,
      },
    ];
  }

  const activities = carePlan.activity;
  let screenignTaskId;
  const updatedActivities = activities!.map((activity) => {
    if (activity.reference!.display === Constants.SCREENING_ACTIVITY) {
      activity.outcomeReference = [{ reference: 'Condition/' + conditionId }];
      const progress = activity.progress ?? [];
      const annotation: Annotation = {
        text: Constants.ACTIVITY_COMPLETED, //mark careplan activity complete
        time: moment().toISOString(),
        authorString: updatedBy,
      };
      progress.push(annotation);
      activity.progress = progress;
      //get the task for the activity
      if (activity.reference && activity.reference.reference) {
        const taskRef = activity.reference.reference;
        screenignTaskId = taskRef.substring(taskRef.indexOf('/') + 1);
      }
    }
    return activity;
  });

  carePlan.activity = updatedActivities;

  //get the screening task
  const taskResponse = await fhirSender.send<Task>('get', 'Task/' + screenignTaskId, undefined);
  task = taskResponse.data;
  if (task) {
    task.status = Constants.TASK_STATUS_COMPLETED; //mark associated task complete
    task = FHIRUtils.addUpdatedBy(task, updatedBy);
  }

  return { task, carePlan };
}
